import { PathFinder, AlgorithmOptions } from '../core/PathFinder';
import { PathResult, AlgorithmStep } from '../core/PathResult';
import { BaseGrid } from '@/grid/BaseGrid';
import { Cell, CellState } from '@/grid/Cell';
import { Heuristic } from '../core/Heuristic';
import { PriorityQueue } from '@/utils/PriorityQueue';

export class BestFirst extends PathFinder {
  readonly name = 'Best-First Search';
  readonly description = 'Greedy search using only h(n) heuristic';

  findPath(grid: BaseGrid, start: Cell, goal: Cell, options?: AlgorithmOptions): PathResult {
    const startTime = performance.now();
    const steps: AlgorithmStep[] = [];

    const generator = this.findPathStepByStep(grid, start, goal, options);

    let result = generator.next();
    while (!result.done) {
      steps.push(result.value);
      result = generator.next();
    }

    const executionTime = performance.now() - startTime;
    const pathResult = result.value;

    return new PathResult(
      pathResult.path,
      pathResult.nodesExplored,
      executionTime,
      pathResult.success,
      steps
    );
  }

  *findPathStepByStep(
    grid: BaseGrid,
    start: Cell,
    goal: Cell,
    options?: AlgorithmOptions
  ): Generator<AlgorithmStep, PathResult, undefined> {
    const startTime = performance.now();
    const heuristic = options?.heuristic || Heuristic.manhattan;

    grid.reset();

    const openSet = new PriorityQueue<Cell>();
    const closedSet = new Set<Cell>();

    start.gCost = 0;
    start.hCost = heuristic(start, goal);
    start.fCost = start.hCost;
    start.parent = null;

    openSet.enqueue(start, start.hCost);
    start.state = CellState.OPEN;

    yield {
      type: 'open',
      cell: start,
      cost: start.hCost,
      openListSize: openSet.size,
      closedListSize: closedSet.size,
      metadata: { gCost: start.gCost, hCost: start.hCost }
    };

    let nodesExplored = 0;

    while (!openSet.isEmpty()) {
      const current = openSet.dequeue();
      if (!current) break;

      if (current.equals(goal)) {
        const path = this.reconstructPath(goal);
        const executionTime = performance.now() - startTime;

        yield {
          type: 'path_found',
          cell: current,
          cost: current.gCost,
          openListSize: openSet.size,
          closedListSize: closedSet.size
        };

        return new PathResult(path, nodesExplored, executionTime, true);
      }

      closedSet.add(current);
      current.state = CellState.CLOSED;
      nodesExplored++;

      yield {
        type: 'close',
        cell: current,
        cost: current.hCost,
        openListSize: openSet.size,
        closedListSize: closedSet.size,
        metadata: { gCost: current.gCost, hCost: current.hCost }
      };

      const neighbors = grid.getNeighbors(current);

      for (const neighbor of neighbors) {
        if (closedSet.has(neighbor)) {
          continue;
        }

        const isInOpenSet = openSet.contains(neighbor, (a, b) => a.equals(b));

        if (!isInOpenSet) {
          neighbor.parent = current;
          neighbor.gCost = current.gCost + grid.calculateDistance(current, neighbor) * neighbor.weight;
          neighbor.hCost = heuristic(neighbor, goal);
          neighbor.fCost = neighbor.hCost;

          openSet.enqueue(neighbor, neighbor.hCost);
          neighbor.state = CellState.OPEN;

          yield {
            type: 'open',
            cell: neighbor,
            cost: neighbor.hCost,
            parent: current,
            openListSize: openSet.size,
            closedListSize: closedSet.size,
            metadata: { gCost: neighbor.gCost, hCost: neighbor.hCost }
          };
        }
      }
    }

    const executionTime = performance.now() - startTime;
    return PathResult.failure(nodesExplored, executionTime);
  }
}
